import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, FileText, Users, Trophy, Eye } from "@phosphor-icons/react";
import { formatCurrency } from "@/lib/currency";

const PAGE_SIZE = 50;

const StatusPill = ({ status, round }) => {
  const color = status === "closed"
    ? "border-black/20 bg-[#0B1929] text-white"
    : round > 1
      ? "border-klein-blue text-[#22D3EE]"
      : "border-black/30 text-black";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-semibold border ${color}`}>
      {status === "closed" ? "Closed" : `Open · R${round}`}
    </span>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = !!user?.is_super_admin;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/auctions", {
          params: { page, page_size: PAGE_SIZE },
        });
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total ?? 0);
        setTotalPages(data.total_pages ?? 1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  // Standard-auction sections. BOP RFQs are also shown to bidders in the
  // Invited section so they don't need to know about the separate menu item.
  const mine = items.filter((i) => i.is_creator && i.type !== "bop");
  const invited = items.filter((i) => !i.is_creator && i.is_invited);
  const platform = items.filter((i) => !i.is_creator && !i.is_invited && i.type !== "bop");

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <TopBar />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Welcome, {user?.name}</div>
            <h1 className="font-display font-extrabold text-5xl lg:text-6xl tracking-tightest leading-none">
              {isAdmin
                ? <>Run a <span className="klein-blue">blind</span> reverse auction.</>
                : <>Submit your <span className="klein-blue">blind</span> quote.</>}
            </h1>
            <p className="text-base text-neutral-600 mt-6 max-w-xl leading-relaxed">
              {isAdmin
                ? "Upload requirements, invite vendors, see only the lowest price — never the identity. Set targets, run rounds, declare a winner."
                : "You can only see auctions you've been invited to and the quotes you've submitted. Other bidders' prices and identities are never shown."}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 justify-end">
            {isAdmin && (
              <Link to="/auctions/new">
                <Button className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp" data-testid="create-auction-cta">
                  <Plus size={16} weight="bold" className="mr-2" />
                  Create Auction
                </Button>
              </Link>
            )}
            <div className="grid grid-cols-3 border border-black/10">
              <Stat icon={<FileText size={14} weight="bold" />} label={isAdmin ? "Created" : "Invited"} value={isAdmin ? mine.length : invited.length} />
              <Stat icon={<Users size={14} weight="bold" />} label={isAdmin ? "Invited" : "Active"} value={isAdmin ? invited.length : invited.filter(i => i.status !== "closed").length} />
              <Stat icon={<Trophy size={14} weight="bold" />} label="Won" value={invited.filter(i => i.status === "closed" && i.winner?.bidder_id === user?.id).length} />
            </div>
          </div>
        </div>

        {isAdmin && <Section title="Auctions I created" testid="my-auctions-section" empty="You haven't created any auctions yet." items={mine} loading={loading} />}
        {isAdmin && <div className="h-10" />}
        <Section
          title={isAdmin ? "Auctions I'm invited to" : "Auctions you're invited to"}
          testid="invited-auctions-section"
          empty="No invitations yet. Auctions will appear here once an admin invites your email."
          items={invited}
          loading={loading}
          bidder
        />
        {isSuperAdmin && (
          <>
            <div className="h-10" />
            <Section
              title="All platform auctions"
              testid="platform-auctions-section"
              empty="No other auctions on the platform yet."
              items={platform}
              loading={loading}
              superAdminBadge
              titleAdornment={
                <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase font-semibold text-[#22D3EE] border border-[#22D3EE] px-1.5 py-0.5 ml-3">
                  <Eye size={10} weight="bold" /> Super Admin
                </span>
              }
            />
          </>
        )}

        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="p-4 border-r border-black/10 last:border-r-0">
    <div className="flex items-center gap-1.5 text-neutral-500 mb-2">
      {icon}
      <span className="text-[9px] tracking-[0.25em] uppercase font-semibold">{label}</span>
    </div>
    <div className="font-display font-extrabold text-3xl tracking-tightest">{value}</div>
  </div>
);

const Section = ({ title, testid, items, loading, empty, bidder, superAdminBadge, titleAdornment }) => (
  <section data-testid={testid}>
    <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
      <h2 className="font-display font-extrabold text-2xl tracking-tightest flex items-baseline">
        {title}
        {titleAdornment}
      </h2>
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{items.length} total</span>
    </div>
    {loading ? (
      <div className="py-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
    ) : items.length === 0 ? (
      <div className="border border-dashed border-black/15 p-10 text-center mt-4">
        <div className="text-sm text-neutral-600">{empty}</div>
      </div>
    ) : (
      <div className="divide-y divide-black/10 border-b border-black/10">
        {items.map((a) => <AuctionRow key={a.id} a={a} bidder={bidder} superAdminBadge={superAdminBadge} />)}
      </div>
    )}
  </section>
);

const AuctionRow = ({ a, bidder, superAdminBadge }) => (
  <Link
    to={`/auctions/${a.id}`}
    className="grid grid-cols-12 gap-4 py-5 hover:bg-neutral-50 transition-colors group items-center px-2"
    data-testid={`auction-row-${a.id}`}
  >
    <div className="col-span-12 md:col-span-6">
      <div className="font-display font-extrabold text-xl tracking-tightest leading-tight flex items-baseline gap-2 flex-wrap">
        {a.title}
        {a.type === "bop" && (
          <span
            className="text-[9px] tracking-[0.25em] uppercase font-semibold text-[#22D3EE] border border-[#22D3EE] px-1.5 py-0.5"
            data-testid={`bop-badge-${a.id}`}
          >
            BOP · {(a.line_items || []).length} item{(a.line_items || []).length === 1 ? "" : "s"}
          </span>
        )}
        {superAdminBadge && (
          <span className="text-[9px] tracking-[0.25em] uppercase font-semibold text-neutral-500 border border-neutral-300 px-1.5 py-0.5">
            by {a.creator_name || "—"}
          </span>
        )}
      </div>
      <div className="text-sm text-neutral-600 mt-1 line-clamp-1">{a.description}</div>
    </div>
    <div className="col-span-6 md:col-span-2">
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Round</div>
      <div className="font-mono-plex text-lg">R{a.current_round}</div>
    </div>
    <div className="col-span-6 md:col-span-2">
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">{bidder ? "Target" : "Bids"}</div>
      <div className="font-mono-plex text-lg">
        {bidder
          ? (a.current_target != null ? formatCurrency(a.current_target, a.currency) : "—")
          : (a.total_bids ?? 0)}
      </div>
    </div>
    <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-3">
      <StatusPill status={a.status} round={a.current_round} />
      <ArrowRight size={16} weight="bold" className="opacity-30 group-hover:opacity-100 transition-opacity" />
    </div>
  </Link>
);

const Pagination = ({ page, totalPages, total, pageSize, onPageChange }) => {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="mt-10 flex items-center justify-between border-t border-black/10 pt-6" data-testid="auctions-pagination">
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
        {from}–{to} of {total}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="rounded-none border border-black/30 h-9 px-4 text-[11px] tracking-[0.25em] uppercase font-semibold btn-sharp disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pagination-prev"
        >
          Prev
        </button>
        <span className="text-xs font-mono-plex px-2">Page {page} / {totalPages}</span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="rounded-none border border-black/30 h-9 px-4 text-[11px] tracking-[0.25em] uppercase font-semibold btn-sharp disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pagination-next"
        >
          Next
        </button>
      </div>
    </div>
  );
};
